import React from "react";
import { cn, type ClassValue } from "@kkp/app-kit";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "ghost"
  | "success"
  | "warning"
  | "danger";
export type ButtonSize = "sm" | "md" | "lg";

export type ButtonProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "color"
> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  className?: string;
};

/**
 * Minimal on-brand button primitive.
 * - Uses existing brand CSS variables from `brand-base.css` / `styles.css`.
 * - Keeps styling consistent with the login page (pill primary, subtle surfaces).
 */
export function Button({
  variant = "primary",
  size = "md",
  fullWidth = false,
  leftIcon,
  rightIcon,
  className,
  disabled,
  children,
  type = "button",
  ...props
}: ButtonProps) {
  const base: ClassValue = [
    "inline-flex items-center justify-center gap-2",
    "select-none whitespace-nowrap",
    "font-bold",
    "rounded-full",
    "transition",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--kkp-magenta)]/60 focus-visible:ring-offset-0",
    "disabled:cursor-not-allowed disabled:opacity-60",
  ];

  const sizing: Record<ButtonSize, ClassValue> = {
    sm: "px-3 py-1.5 text-xs",
    md: "px-4 py-2 text-sm",
    lg: "px-5 py-2.5 text-sm",
  };

  const variants: Record<ButtonVariant, ClassValue> = {
    primary: [
      "text-white",
      "border border-transparent",
      "bg-gradient-to-br from-[color:var(--kkp-purple)] to-[color:var(--kkp-magenta)]",
      "shadow-[0_10px_18px_rgba(0,0,0,0.22),_inset_0_1px_0_rgba(255,255,255,0.15)]",
      "hover:brightness-[1.05]",
      "active:translate-y-[1px]",
    ],
    success: [
      "text-white",
      "border border-transparent",
      /* keep the SAME gradient structure as primary, but tint it for success */
      "bg-gradient-to-br from-[color:color-mix(in_oklab,var(--kkp-success)_55%,var(--kkp-purple))] to-[color:color-mix(in_oklab,var(--kkp-success)_55%,var(--kkp-magenta))]",
      "shadow-[0_10px_18px_rgba(0,0,0,0.22),_inset_0_1px_0_rgba(255,255,255,0.15)]",
      "hover:brightness-[1.05]",
      "active:translate-y-[1px]",
    ],
    warning: [
      "text-white",
      "border border-transparent",
      /* keep the SAME gradient structure as primary, but tint it for warning */
      "bg-gradient-to-br from-[color:color-mix(in_oklab,var(--kkp-warning)_55%,var(--kkp-purple))] to-[color:color-mix(in_oklab,var(--kkp-warning)_55%,var(--kkp-magenta))]",
      "shadow-[0_10px_18px_rgba(0,0,0,0.22),_inset_0_1px_0_rgba(255,255,255,0.15)]",
      "hover:brightness-[1.05]",
      "active:translate-y-[1px]",
    ],
    secondary: [
      "text-white",
      "border border-[color:var(--kkp-border)]",
      "bg-[color:var(--kkp-surface)]",
      "hover:bg-[color:var(--kkp-surface-2)]",
      "active:translate-y-[1px]",
    ],
    ghost: [
      "text-white",
      "border border-transparent",
      "bg-transparent",
      "hover:bg-[color:var(--kkp-surface)]",
      "active:bg-[color:var(--kkp-surface-2)]",
    ],
    danger: [
      "text-white",
      "border border-transparent",
      /* keep the SAME gradient structure as primary, but tint it for danger */
      "bg-gradient-to-br from-[color:color-mix(in_oklab,var(--kkp-danger)_55%,var(--kkp-purple))] to-[color:color-mix(in_oklab,var(--kkp-danger)_55%,var(--kkp-magenta))]",
      "shadow-[0_10px_18px_rgba(0,0,0,0.22),_inset_0_1px_0_rgba(255,255,255,0.15)]",
      "hover:brightness-[1.05]",
      "active:translate-y-[1px]",
    ],
  };

  return (
    <button
      type={type}
      className={cn(
        base,
        sizing[size],
        variants[variant],
        fullWidth && "w-full",
        className,
      )}
      disabled={disabled}
      {...props}
    >
      {leftIcon ? (
        <span className="inline-flex shrink-0">{leftIcon}</span>
      ) : null}
      <span className="inline-flex min-w-0 items-center">{children}</span>
      {rightIcon ? (
        <span className="inline-flex shrink-0">{rightIcon}</span>
      ) : null}
    </button>
  );
}
